import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export type CloseReason = 'bought_back' | 'expired' | 'assigned';

interface ClosePositionPopupProps {
  open: boolean;
  ticker: string;
  strike: string;
  expiry: string;
  onClose: () => void;
  onConfirm: (data: { close_reason: CloseReason; close_date: string; close_price?: number }) => Promise<void>;
}

const ClosePositionPopup: React.FC<ClosePositionPopupProps> = ({
  open, ticker, strike, expiry, onClose, onConfirm,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [reason, setReason] = useState<CloseReason>('expired');
  const [closeDate, setCloseDate] = useState(new Date().toISOString().split('T')[0]);
  const [closePrice, setClosePrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (reason === 'bought_back' && (!closePrice || isNaN(parseFloat(closePrice)))) {
      setError('Enter the buyback price per share');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onConfirm({
        close_reason: reason,
        close_date: closeDate,
        close_price: reason === 'bought_back' ? parseFloat(closePrice) : undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to close position');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="xs" fullWidth>
      <DialogTitle>Close Position</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {ticker} ${strike} — {expiry}
        </Typography>

        <FormControl component="fieldset" sx={{ mt: 1 }}>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value as CloseReason)}>
            <FormControlLabel value="expired" control={<Radio size="small" />} label="Expired worthless" />
            <FormControlLabel value="assigned" control={<Radio size="small" />} label="Assigned" />
            <FormControlLabel value="bought_back" control={<Radio size="small" />} label="Bought back" />
          </RadioGroup>
        </FormControl>

        {reason === 'bought_back' && (
          <TextField
            label="Buyback price (per share)"
            type="number"
            value={closePrice}
            onChange={(e) => setClosePrice(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
          />
        )}

        <TextField
          label="Close date"
          type="date"
          value={closeDate}
          onChange={(e) => setCloseDate(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Saving…' : 'Close Position'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosePositionPopup;
